import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageLayout from '../components/PageLayout';
import Button from '../components/ui/Button';
import FileButton from '../components/ui/FileButton';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';
import { createArticle, getAuthoringDepartments, uploadMedia } from '../api';
import { findRiseCourses, importRiseCourse, sourceFromFileList } from '../import/riseImport';
import { readZip } from '../import/zipReader';
import { putImportDraft } from '../import/importHandoff';
import { titleErrorMessage } from '../utils/articleTitle';

/**
 * "Inhalt erstellen" from Articulate Rise 360 web exports: the author picks a
 * folder or a ZIP, and every course in it is converted in the browser
 * (import/riseImport.js).
 *
 * One course opens in the ordinary editor, where it is reviewed, given a
 * department and published; nothing is uploaded before that. Several courses
 * become one article each in a single run: they are converted, uploaded and
 * published one after another, so only one course is in memory at a time,
 * and are reviewed afterwards under "Meine Veröffentlichungen".
 */
function CreateArticleRise() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [courses, setCourses] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [outcomes, setOutcomes] = useState(null);

  function reset() {
    setError('');
    setResult(null);
    setCourses(null);
    setSelected(new Set());
    setProgress(null);
    setOutcomes(null);
  }

  async function convert(loadSource) {
    setBusy(true);
    reset();

    try {
      const found = await findRiseCourses(await loadSource());

      if (found.length === 0) {
        throw new Error(t('createArticleRise.noCourse'));
      }

      if (found.length === 1) {
        setResult(await importRiseCourse(await found[0].open()));
        return;
      }

      setCourses(found);
      setSelected(new Set(found.map((_, index) => index)));
      setDepartmentId(null);
      getAuthoringDepartments(false)
        .then((list) => setDepartments(list ?? []))
        .catch((loadError) => {
          // As in the editor: without a list the server picks the department.
          console.error('Failed to load authoring departments:', loadError);
        });
    } catch (err) {
      console.error('Rise import failed:', err);
      setError(err?.message || t('createArticleRise.error'));
    } finally {
      setBusy(false);
    }
  }

  function handleFolder(event) {
    const { files } = event.target;
    if (files?.length) {
      convert(async () => sourceFromFileList(files));
    }
    event.target.value = '';
  }

  function handleZip(event) {
    const file = event.target.files?.[0];
    if (file) {
      convert(() => readZip(file));
    }
    event.target.value = '';
  }

  function openInEditor() {
    const importId = putImportDraft(result);
    navigate('/create-article/manual', { state: { importId } });
  }

  function toggleCourse(index) {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  // One course: converted, its media uploaded, the article created. Media
  // that fail to upload are left out with a warning, not the whole course.
  async function createFromCourse(course) {
    const draft = await importRiseCourse(await course.open());
    const warnings = [...draft.warnings];
    let text = draft.text;

    for (const { placeholder, file } of draft.files) {
      let url = '';
      try {
        ({ url } = await uploadMedia(file, 'article'));
      } catch (uploadError) {
        warnings.push(t('createArticleRise.uploadFailed', {
          name: file.name,
          reason: uploadError?.message ?? '',
        }));
      }
      text = text.split(placeholder).join(url);
    }

    const article = await createArticle({
      title: draft.title,
      text,
      summary: draft.summary,
      tags: [],
      icon: null,
      ...(departmentId === null ? {} : { department_id: departmentId }),
    });

    return { title: draft.title, articleId: article.article_id, warnings };
  }

  async function createAll() {
    if (departments.length > 1 && departmentId === null) {
      setError(t('createArticle.pickDepartment'));
      return;
    }

    const chosen = courses.filter((_, index) => selected.has(index));
    const done = [];

    setBusy(true);
    setError('');

    for (const [index, course] of chosen.entries()) {
      setProgress({ current: index + 1, total: chosen.length, title: course.title });

      try {
        done.push(await createFromCourse(course));
      } catch (err) {
        console.error(`Rise import of "${course.title}" failed:`, err);
        done.push({
          title: course.title,
          error: titleErrorMessage(err, course.title, t) || err?.message || t('createArticleRise.error'),
          warnings: [],
        });
      }

      setOutcomes([...done]);
    }

    setProgress(null);
    setCourses(null);
    setBusy(false);
  }

  const pictureCount = result?.files.filter((f) => f.type === 'image').length ?? 0;
  const otherCount = (result?.files.length ?? 0) - pictureCount;
  const createdCount = outcomes?.filter((outcome) => outcome.articleId).length ?? 0;

  return (
    <PageLayout showSearch showAiSearch>
      <PageMain className="article-page">
        <PageHeader title={t('createArticleRise.title')} condensed />

        <PageBody className="page-body--split">
          <div className="maincol markdown-content">
            <div className="pdf-upload-box">
              <i className="fa-solid fa-graduation-cap" />
              <h2>{t('createArticleRise.selectTitle')}</h2>
              <p>{t('createArticleRise.description')}</p>

              <div className="rise-import-actions">
                <FileButton variant="choice" directory disabled={busy} onChange={handleFolder}>
                  <i className="fa-solid fa-folder-open" />
                  {t('createArticleRise.folderButton')}
                </FileButton>

                <FileButton variant="choice" accept=".zip,application/zip" disabled={busy} onChange={handleZip}>
                  <i className="fa-solid fa-file-zipper" />
                  {t('createArticleRise.zipButton')}
                </FileButton>
              </div>

              {busy && !progress && <p>{t('createArticleRise.processing')}</p>}

              {error && (
                <div className="login-message login-message-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" />
                  <span>{error}</span>
                </div>
              )}

              {result && (
                <div className="rise-import-result">
                  <h3>{result.title}</h3>
                  <p>
                    {t('createArticleRise.found', {
                      pictures: pictureCount,
                      media: otherCount,
                      words: result.text.split(/\s+/).filter(Boolean).length,
                    })}
                  </p>

                  {result.warnings.length > 0 && (
                    <ul className="rise-import-warnings">
                      {result.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  )}

                  <Button variant="choice" onClick={openInEditor}>
                    <i className="fa-solid fa-pen-to-square" />
                    {t('createArticleRise.openEditor')}
                  </Button>
                </div>
              )}

              {courses && (
                <div className="rise-import-result">
                  <h3>{t('createArticleRise.coursesFound', { count: courses.length })}</h3>
                  <p>{t('createArticleRise.batchNote')}</p>

                  <ul className="rise-import-courses">
                    {courses.map((course, index) => (
                      <li key={`${course.name}-${index}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={selected.has(index)}
                            disabled={busy}
                            onChange={() => toggleCourse(index)}
                          />
                          <span>{course.title}</span>
                          {course.name && course.name !== course.title && (
                            <small>{course.name}</small>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>

                  {departments.length > 1 && (
                    <label className="rise-import-department">
                      <span>{t('articleEditor.departmentLabel')}</span>
                      <select
                        className="article-scope-select"
                        value={departmentId ?? ''}
                        disabled={busy}
                        onChange={(event) => setDepartmentId(
                          event.target.value === '' ? null : Number(event.target.value),
                        )}
                      >
                        <option value="">{t('articleEditor.departmentPlaceholder')}</option>
                        {departments.map((department) => (
                          <option key={department.department_id} value={department.department_id}>
                            {department.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {progress ? (
                    <p role="status">
                      {t('createArticleRise.creating', progress)}
                    </p>
                  ) : (
                    <Button
                      variant="choice"
                      disabled={busy || selected.size === 0}
                      onClick={createAll}
                    >
                      <i className="fa-solid fa-layer-group" />
                      {t('createArticleRise.createAll', { count: selected.size })}
                    </Button>
                  )}
                </div>
              )}

              {outcomes && !courses && (
                <div className="rise-import-result">
                  <h3>
                    {t('createArticleRise.batchDone', {
                      created: createdCount,
                      total: outcomes.length,
                    })}
                  </h3>

                  <ul className="rise-import-courses">
                    {outcomes.map((outcome, index) => (
                      <li key={`${outcome.title}-${index}`}>
                        {outcome.articleId ? (
                          <Link to={`/article/${outcome.articleId}`}>{outcome.title}</Link>
                        ) : (
                          <span>
                            {outcome.title}: <strong>{outcome.error}</strong>
                          </span>
                        )}

                        {outcome.warnings.length > 0 && (
                          <details>
                            <summary>
                              {t('createArticleRise.warningsCount', { count: outcome.warnings.length })}
                            </summary>
                            <ul className="rise-import-warnings">
                              {outcome.warnings.map((warning) => (
                                <li key={warning}>{warning}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </li>
                    ))}
                  </ul>

                  <Button variant="choice" to="/my-articles">
                    <i className="fa-solid fa-pen-to-square" />
                    {t('createArticleRise.toMyArticles')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PageBody>
      </PageMain>
    </PageLayout>
  );
}

export default CreateArticleRise;
